import { useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Add, ArrowForward, Block, GroupsOutlined } from '@mui/icons-material';
import * as usersApi from '@/api/users';
import { isUsable, listProductSubscriptions } from '@/api/products';
import type { TenantUser } from '@/api/users';
import type { TenantUserResponse } from '@/api/types';
import { toApiError } from '@/api/client';
import { useAuth } from '@/auth/useAuth';
import { useProduct } from '@/auth/ProductContext';
import { usePermissions } from '@/hooks/usePermissions';
import { LoadingScreen } from '@/components/LoadingScreen';
import { EmptyState } from '@/components/EmptyState';
import { UserFilters, type RoleFilter, type StatusFilter } from '@/components/UserFilters';
import { UserList } from '@/components/UserList';
import { UsersCardList } from '@/components/UsersCardList';
import { UsersEmptyIllustration } from '@/components/UsersEmptyIllustration';
import {
  InviteUserDialog,
  invitationProductName,
  type InviteUserSubmitValues,
} from '@/components/InviteUserDialog';
import {
  ChangeUserRoleDialog,
  type ChangeRoleSubmitValues,
} from '@/components/ChangeUserRoleDialog';
import { ScopeDialog, type ScopeSubmitValues } from '@/components/ScopeDialog';
import { DisableUserDialog } from '@/components/DisableUserDialog';
import { BulkDisableDialog } from '@/components/BulkDisableDialog';
import { ConfirmDialog } from '@/components/ConfirmDialog';

export const Route = createFileRoute('/_authenticated/users/')({
  component: UsersListPage,
});

type Toast = { message: string; severity: 'success' | 'error' } | null;

function UsersListPage(): JSX.Element {
  const { user } = useAuth();
  const { activeProduct } = useProduct();
  const permissions = usePermissions();
  const queryClient = useQueryClient();
  const theme = useTheme();
  // Mobile breakpoint: el design usa 720px. MUI `sm` por default es 600, así que usamos
  // una query custom para acercarnos al threshold del comp visual.
  const isMobile = useMediaQuery('(max-width:719.95px)');

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
  const [selected, setSelected] = useState<Set<string>>(() => new Set());

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const [roleDialogUser, setRoleDialogUser] = useState<TenantUserResponse | null>(null);
  const [roleError, setRoleError] = useState<string | null>(null);

  const [scopeDialogUser, setScopeDialogUser] = useState<TenantUserResponse | null>(null);
  const [scopeError, setScopeError] = useState<string | null>(null);

  const [disableDialogUser, setDisableDialogUser] = useState<TenantUserResponse | null>(null);
  const [disableError, setDisableError] = useState<string | null>(null);

  const [bulkDisableOpen, setBulkDisableOpen] = useState(false);
  const [bulkDisableError, setBulkDisableError] = useState<string | null>(null);

  const [reenableDialogUser, setReenableDialogUser] = useState<TenantUserResponse | null>(null);

  const [toast, setToast] = useState<Toast>(null);

  const usersQ = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list({ skip: 0, take: 200 }),
  });

  const subscriptionsQ = useQuery({
    queryKey: ['product-subscriptions', user?.tenantId],
    queryFn: listProductSubscriptions,
    enabled: Boolean(user?.tenantId),
    staleTime: 5 * 60_000,
  });

  const invalidate = (): void => {
    queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  const inviteMut = useMutation({
    mutationFn: (body: InviteUserSubmitValues) => usersApi.invite(body),
    onSuccess: (created) => {
      invalidate();
      setInviteOpen(false);
      setInviteError(null);
      setToast({ message: `Usuario invitado: ${created.email}`, severity: 'success' });
    },
    onError: (err) => {
      setInviteError(toApiError(err).message);
    },
  });

  const roleMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: ChangeRoleSubmitValues }) =>
      usersApi.changeRole(id, body),
    onSuccess: (updated) => {
      invalidate();
      setRoleDialogUser(null);
      setRoleError(null);
      setToast({ message: `Responsabilidad actualizada: ${updated.email}`, severity: 'success' });
    },
    onError: (err) => {
      setRoleError(toApiError(err).message);
    },
  });

  const scopeMut = useMutation({
    mutationFn: ({
      id,
      role,
      body,
    }: {
      id: string;
      role: ChangeRoleSubmitValues['role'];
      body: ScopeSubmitValues;
    }) => usersApi.changeRole(id, { role, cuitProfileScope: body.cuitProfileScope }),
    onSuccess: (updated) => {
      invalidate();
      setScopeDialogUser(null);
      setScopeError(null);
      setToast({ message: `Cartera actualizada: ${updated.email}`, severity: 'success' });
    },
    onError: (err) => {
      setScopeError(toApiError(err).message);
    },
  });

  const disableMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string | undefined }) =>
      usersApi.disable(id, reason),
    onSuccess: (_, vars) => {
      const target = users.find((u) => u.id === vars.id);
      invalidate();
      setDisableDialogUser(null);
      setDisableError(null);
      setToast({
        message: target?.status === 'Invited' ? 'Invitación revocada.' : 'Usuario deshabilitado.',
        severity: 'success',
      });
    },
    onError: (err) => {
      setDisableError(toApiError(err).message);
    },
  });

  const bulkDisableMut = useMutation({
    mutationFn: ({ ids, reason }: { ids: string[]; reason: string | undefined }) =>
      usersApi.bulkDisable(ids, reason),
    onSuccess: (_, vars) => {
      invalidate();
      setBulkDisableOpen(false);
      setBulkDisableError(null);
      setSelected(new Set());
      setToast({
        message: `${vars.ids.length} usuario(s) deshabilitado(s).`,
        severity: 'success',
      });
    },
    onError: (err) => {
      setBulkDisableError(toApiError(err).message);
    },
  });

  const reenableMut = useMutation({
    mutationFn: (id: string) => usersApi.reenable(id),
    onSuccess: () => {
      invalidate();
      setReenableDialogUser(null);
      setToast({ message: 'Usuario reactivado.', severity: 'success' });
    },
    onError: (err) => {
      setToast({ message: toApiError(err).message, severity: 'error' });
    },
  });

  // Augmentamos cada user con el campo derivado `lastLogin` (todavía no lo emite el backend).
  const users = useMemo<TenantUser[]>(
    () => (usersQ.data?.items ?? []).map(usersApi.withLastLogin),
    [usersQ.data],
  );

  const teamProduct = activeProduct === 'Admin' ? 'Empresa' : activeProduct;
  const subscription = subscriptionsQ.data?.find(
    (item) => item.product === teamProduct && isUsable(item),
  );
  const productName = invitationProductName(teamProduct, subscription?.tier);
  const maxUsers = subscription?.limits['max-users'] ?? (teamProduct === 'Empresa' ? 1 : undefined);
  const activeOrInvitedUsers = users.filter((item) => item.status !== 'Disabled').length;
  const teamLimitReached =
    maxUsers !== undefined && maxUsers !== -1 && activeOrInvitedUsers >= maxUsers;

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (q && !u.email.toLowerCase().includes(q) && !u.name.toLowerCase().includes(q)) {
        return false;
      }
      if (roleFilter && u.role !== roleFilter) return false;
      if (statusFilter && u.status !== statusFilter) return false;
      return true;
    });
  }, [users, search, roleFilter, statusFilter]);

  const hasActiveFilters = search !== '' || roleFilter !== '' || statusFilter !== '';

  const clearFilters = (): void => {
    setSearch('');
    setRoleFilter('');
    setStatusFilter('');
  };

  // ── Bulk select helpers ───────────────────────────────────────────────────
  const selectableIds = useMemo(
    () =>
      filteredUsers
        .filter((u) => u.id !== user?.id && u.status !== 'Disabled')
        .map((u) => u.id),
    [filteredUsers, user?.id],
  );

  const toggleSelect = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (): void => {
    setSelected((prev) => {
      const allCurrentlySelected =
        selectableIds.length > 0 && selectableIds.every((id) => prev.has(id));
      if (allCurrentlySelected) return new Set();
      return new Set(selectableIds);
    });
  };

  const clearSelection = (): void => setSelected(new Set());

  if (usersQ.isLoading) return <LoadingScreen message="Cargando usuarios…" />;

  if (usersQ.isError) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => usersQ.refetch()}>
            Reintentar
          </Button>
        }
      >
        {toApiError(usersQ.error).message}
      </Alert>
    );
  }

  const isEmpty = users.length === 0;
  const noResults = !isEmpty && filteredUsers.length === 0;

  const inviteButton = teamLimitReached ? (
    <Button
      component={Link}
      to="/billing"
      variant="contained"
      startIcon={<GroupsOutlined />}
      endIcon={<ArrowForward />}
      disabled={!permissions.canManageUsers}
    >
      Ampliar equipo
    </Button>
  ) : (
    <Button
      variant="contained"
      startIcon={<Add />}
      onClick={() => {
        setInviteError(null);
        setInviteOpen(true);
      }}
      disabled={!permissions.canManageUsers}
    >
      Sumar persona
    </Button>
  );

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        sx={{ mb: 3, gap: 2 }}
      >
        <Box>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" useFlexGap>
            <Typography variant="h4">Equipo</Typography>
            <Chip size="small" color="primary" variant="outlined" label={productName} />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Organizá quién trabaja en {productName} y qué responsabilidad tiene cada persona.
          </Typography>
        </Box>
        {!isEmpty &&
          (permissions.canManageUsers ? (
            inviteButton
          ) : (
            <Tooltip title="Solo el administrador puede invitar">
              <span>{inviteButton}</span>
            </Tooltip>
          ))}
      </Stack>

      {teamLimitReached && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={
            permissions.canManageUsers ? (
              <Button
                component={Link}
                to="/billing"
                color="inherit"
                size="small"
              >
                Ver opciones
              </Button>
            ) : undefined
          }
        >
          Tu plan {subscription?.tierName ?? 'actual'} incluye {maxUsers === 1 ? 'una persona' : `${maxUsers} personas`}.
          Para sumar colaboradores, ampliá el plan de {productName}.
        </Alert>
      )}

      {isEmpty ? (
        <EmptyState
          icon={<UsersEmptyIllustration />}
          title="Tu equipo empieza acá"
          description={`Sumá personas para repartir las tareas de ${productName} sin compartir accesos.`}
          actionLabel={permissions.canManageUsers && !teamLimitReached ? 'Sumar primera persona' : undefined}
          onAction={
            permissions.canManageUsers && !teamLimitReached
              ? () => {
                  setInviteError(null);
                  setInviteOpen(true);
                }
              : undefined
          }
        />
      ) : (
        <>
          <UserFilters
            search={search}
            role={roleFilter}
            status={statusFilter}
            onSearchChange={setSearch}
            onRoleChange={setRoleFilter}
            onStatusChange={setStatusFilter}
          />

          {/* Bulk action bar — visible solo cuando hay al menos uno seleccionado. */}
          {permissions.canManageUsers && selected.size > 0 && (
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                borderLeft: `3px solid ${theme.palette.primary.main}`,
                bgcolor: theme.palette.action.selected,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>
                {selected.size} seleccionado{selected.size === 1 ? '' : 's'}
              </Typography>
              <Button
                size="small"
                color="error"
                startIcon={<Block />}
                onClick={() => {
                  setBulkDisableError(null);
                  setBulkDisableOpen(true);
                }}
              >
                Deshabilitar
              </Button>
              <Button size="small" onClick={clearSelection}>
                Cancelar
              </Button>
            </Paper>
          )}

          {noResults ? (
            <EmptyState
              icon={<UsersEmptyIllustration />}
              title="Sin resultados"
              description="Probá ajustar los filtros o limpialos para ver todos los usuarios."
              actionLabel="Limpiar filtros"
              onAction={clearFilters}
            />
          ) : isMobile ? (
            <UsersCardList
              users={filteredUsers}
              currentUserId={user?.id ?? ''}
              permissions={permissions}
              selected={selected}
              onToggleSelect={toggleSelect}
              onChangeRole={(u) => {
                setRoleError(null);
                setRoleDialogUser(u);
              }}
              onChangeScope={(u) => {
                setScopeError(null);
                setScopeDialogUser(u);
              }}
              onDisable={(u) => {
                setDisableError(null);
                setDisableDialogUser(u);
              }}
              onReenable={(u) => setReenableDialogUser(u)}
            />
          ) : (
            <UserList
              users={filteredUsers}
              currentUserId={user?.id ?? ''}
              permissions={permissions}
              selected={selected}
              onToggleSelect={toggleSelect}
              onToggleSelectAll={toggleSelectAll}
              onChangeRole={(u) => {
                setRoleError(null);
                setRoleDialogUser(u);
              }}
              onChangeScope={(u) => {
                setScopeError(null);
                setScopeDialogUser(u);
              }}
              onDisable={(u) => {
                setDisableError(null);
                setDisableDialogUser(u);
              }}
              onReenable={(u) => setReenableDialogUser(u)}
            />
          )}

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            {hasActiveFilters
              ? `Mostrando ${filteredUsers.length} de ${users.length} usuarios`
              : `${users.length} usuario${users.length === 1 ? '' : 's'}`}
          </Typography>
        </>
      )}

      <InviteUserDialog
        open={inviteOpen}
        product={teamProduct}
        subscription={subscription}
        submitting={inviteMut.isPending}
        errorMessage={inviteError}
        onSubmit={(values) => {
          setInviteError(null);
          inviteMut.mutate(values);
        }}
        onCancel={() => {
          if (!inviteMut.isPending) setInviteOpen(false);
        }}
      />

      <ChangeUserRoleDialog
        open={roleDialogUser !== null}
        user={roleDialogUser}
        submitting={roleMut.isPending}
        errorMessage={roleError}
        onSubmit={(values) => {
          if (!roleDialogUser) return;
          setRoleError(null);
          roleMut.mutate({ id: roleDialogUser.id, body: values });
        }}
        onCancel={() => {
          if (!roleMut.isPending) setRoleDialogUser(null);
        }}
      />

      <ScopeDialog
        open={scopeDialogUser !== null}
        user={scopeDialogUser}
        submitting={scopeMut.isPending}
        errorMessage={scopeError}
        onSubmit={(values) => {
          if (!scopeDialogUser) return;
          setScopeError(null);
          scopeMut.mutate({
            id: scopeDialogUser.id,
            role: scopeDialogUser.role,
            body: values,
          });
        }}
        onCancel={() => {
          if (!scopeMut.isPending) setScopeDialogUser(null);
        }}
      />

      <DisableUserDialog
        open={disableDialogUser !== null}
        user={disableDialogUser}
        submitting={disableMut.isPending}
        errorMessage={disableError}
        onConfirm={(reason) => {
          if (!disableDialogUser) return;
          setDisableError(null);
          disableMut.mutate({ id: disableDialogUser.id, reason });
        }}
        onCancel={() => {
          if (!disableMut.isPending) setDisableDialogUser(null);
        }}
      />

      <BulkDisableDialog
        open={bulkDisableOpen}
        count={selected.size}
        submitting={bulkDisableMut.isPending}
        errorMessage={bulkDisableError}
        onConfirm={(reason) =>
          bulkDisableMut.mutate({ ids: Array.from(selected), reason })
        }
        onCancel={() => {
          if (!bulkDisableMut.isPending) setBulkDisableOpen(false);
        }}
      />

      <ConfirmDialog
        open={reenableDialogUser !== null}
        title={`¿Reactivar a ${reenableDialogUser?.name ?? ''}?`}
        description="El usuario va a recuperar el acceso al portal con su rol anterior."
        confirmLabel="Reactivar"
        loading={reenableMut.isPending}
        onConfirm={() => {
          if (reenableDialogUser) reenableMut.mutate(reenableDialogUser.id);
        }}
        onCancel={() => {
          if (!reenableMut.isPending) setReenableDialogUser(null);
        }}
      />

      <Snackbar
        open={toast !== null}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
